import React, {useEffect, useState} from "react";
import {Alert, Button, Card, Col, Form, InputNumber, Row, Select, Statistic, Typography} from 'antd';
import {
    CheckOutlined,
    FlagOutlined,
    InfoCircleOutlined,
    MobileOutlined,
    QrcodeOutlined,
    TeamOutlined,
    TrophyOutlined,
    UserOutlined,
    GiftOutlined
} from '@ant-design/icons';
import MobileDebugInfo from "../components/debug/MobileDebugInfo";
import AvatarDisplay from "../components/AvatarDisplay";

const { Title, Text} = Typography;
const { Option } = Select;

const MobileTerminal = ({
                            players,
                            holes,
                            api,
                            queryParams,
                            showToast,
                            showSuccess,
                            showError,
                            showWarning,
                            showInfo
                        }) => {

    const nfcTagId = queryParams.nfc;

    const [selectedHole, setSelectedHole] = useState(1);
    const [currentPlayer, setCurrentPlayer] = useState(null);
    const [playerScores, setPlayerScores] = useState({});
    const [bonusScore, setBonusScore] = useState(0);
    const [holeData, setHoleData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        const loadHoleData = async () => {
            try {
                const hole = await api.getHole(selectedHole);
                setHoleData(hole);
            } catch (error) {
                console.error('Błąd ładowania dołka:', error);
                showError(`Błąd ładowania danych dołka #${selectedHole} ❌`);
            }
        };

        const handleNfc = async () => {
            if (!nfcTagId) {
                showError('Brak ID gracza w linku! Zeskanuj ponownie kod QR ');
                return;
            }

            setLoading(true);

            try {
                const player = await api.getPlayer(nfcTagId);
                setCurrentPlayer(player);

                if (player) {
                    const defaultStrokes = holeData?.par || 3;
                    const initialScores = {};
                    player.playerNames.forEach(name => {
                        initialScores[name] = defaultStrokes;
                    });
                    setPlayerScores(initialScores);
                    form.setFieldsValue(initialScores);
                }
            } catch (error) {
                showError(`Błąd ładowania gracza: ${error.message} ❌`);
                setCurrentPlayer(null);
            } finally {
                setLoading(false);
            }
        };

        if (selectedHole) {
            loadHoleData();
        }

        if (nfcTagId) {
            handleNfc();
        }

    }, [nfcTagId, selectedHole, api, showError, holeData?.par, form]);

    const handleScoreSubmit = async () => {
        if (!currentPlayer) {
            showError('Nie znaleziono gracza! Upewnij się, że masz poprawny link z recepcji ');
            return;
        }

        // Walidacja wyników - sprawdź wszystkich graczy
        const allPlayerNames = currentPlayer.playerNames;
        const missingScores = allPlayerNames.filter(name => !playerScores[name] || playerScores[name] < 1);

        if (missingScores.length > 0) {
            showWarning(`Proszę wprowadzić wyniki dla wszystkich graczy: ${missingScores.join(', ')} ⛳`);
            return;
        }

        // Sprawdź, czy wszystkie wyniki są prawidłowe - max 6 ruchów
        const invalidScores = allPlayerNames.filter(name => !playerScores[name] || playerScores[name] < 1 || playerScores[name] > 6);
        if (invalidScores.length > 0) {
            showWarning('Wszystkie wyniki muszą być liczbą między 1 a 6 ruchów ⛳');
            return;
        }

        const calculatePoints = (strokes, par, holeBonus = 0) => {
            // Podstawowe obliczenie: strokes - par
            const basePoints = strokes - par;

            // Ograniczenie do zakresu -4 do +6
            const limitedPoints = Math.max(-4, Math.min(6, basePoints));

            // Dodanie bonusu z dołka (może być ujemny - zmniejsza punkty)
            const finalPoints = limitedPoints + holeBonus;

            return finalPoints;
        };

        // Stwórz listę wyników z obliczonymi punktami
        const playerScoresList = allPlayerNames.map(playerName => {
            const strokes = parseInt(playerScores[playerName]);
            const par = holeData?.par || 3;
            const holeBonus = holeData?.withBonus ? (bonusScore || 0) : 0;
            const points = calculatePoints(strokes, par, holeBonus);

            return {
                playerName,
                strokes: strokes,
                points: points
            };
        });

        setLoading(true);

        try {
            console.log('Submitting group scores from mobile with calculated points...', {
                nfcTagId: currentPlayer.nfcTagId,
                holeNumber: selectedHole,
                playerScores: playerScoresList,
                holeBonus: holeData?.withBonus ? bonusScore : 0
            });

            // Użyj metody submitGroupScore z gotowymi punktami
            await api.submitGroupScore({
                nfcTagId: currentPlayer.nfcTagId,
                holeNumber: selectedHole,
                playerScores: playerScoresList
            });

            const totalPoints = playerScoresList.reduce((sum, p) => sum + p.points, 0);
            const totalStrokes = playerScoresList.reduce((sum, p) => sum + p.strokes, 0);
            const bonusInfo = holeData?.withBonus && bonusScore !== 0 ? ` (bonus: ${bonusScore > 0 ? '+' : ''}${bonusScore})` : '';

            showSuccess(`Wyniki grupy zapisane! ${totalStrokes} ruchów = ${totalPoints} punktów${bonusInfo} `);

            // Reset formularza
            setPlayerScores({});
            setBonusScore(0);
            form.resetFields();

            const updatedPlayer = await api.getPlayer(nfcTagId);
            setCurrentPlayer(updatedPlayer);

            const updatedHole = await api.getHole(selectedHole);
            setHoleData(updatedHole);

        } catch (error) {
            showError(`Błąd zapisywania wyniku: ${error.message} ❌`);
        } finally {
            setLoading(false);
        }
    };

    const handleHoleChange = (holeNumber) => {
        setSelectedHole(holeNumber);
        setPlayerScores({});
        setBonusScore(0);
        form.resetFields();

        // Jeśli mamy gracza, zainicjalizuj wyniki dla nowego dołka
        if (currentPlayer) {
            const defaultStrokes = holes.find(h => h.number === holeNumber)?.par || 3;
            const initialScores = {};
            currentPlayer.playerNames.forEach(name => {
                initialScores[name] = defaultStrokes;
            });
            setPlayerScores(initialScores);
            form.setFieldsValue(initialScores);
        }
    };

    const handleScoreChange = (playerName, value) => {
        setPlayerScores(prev => ({
            ...prev,
            [playerName]: value
        }));

    };

    const handleBonusChange = (value) => {
        setBonusScore(value || 0);
    };

    const isFormComplete = currentPlayer &&
        currentPlayer.playerNames &&
        currentPlayer.playerNames.length > 0 &&
        currentPlayer.playerNames.every(name => playerScores[name] && playerScores[name] >= 1);

    const getIndividualPlayersLeaderboard = () => {
        if (!players || players.length === 0) return [];

        const individualPlayers = [];

        players.forEach(group => {
            if (group.playerNames && group.playerNames.length > 0) {
                group.playerNames.forEach(playerName => {
                    // Znajdź indywidualne wyniki gracza ze wszystkich dołków
                    let totalScore = 0;
                    let holesPlayed = 0;
                    let totalStrokes = 0;

                    if (group.holes && group.holes.length > 0) {
                        group.holes.forEach(hole => {
                            if (hole.playerResults && hole.playerResults.length > 0) {
                                const playerResult = hole.playerResults.find(pr => pr.playerName === playerName);
                                if (playerResult) {
                                    totalScore += playerResult.points || 0;
                                    totalStrokes += playerResult.strokes || 0;
                                    holesPlayed++;
                                }
                            }
                        });
                    }

                    individualPlayers.push({
                        playerName: playerName,
                        groupName: group.groupName,
                        groupId: group.nfcTagId,
                        avatar: group.avatar,
                        avatarId: group.avatar?.numericId || group.avatarId,
                        totalScore: totalScore,
                        totalStrokes: totalStrokes,
                        holesPlayed: holesPlayed,
                        isCurrentPlayer: group.nfcTagId === currentPlayer?.nfcTagId
                    });
                });
            }
        });

        return individualPlayers.sort((a, b) => {
            if (a.totalScore !== b.totalScore) {
                return a.totalScore - b.totalScore;
            }
            return a.totalStrokes - b.totalStrokes;
        });
    };

    return (
        <div className="container">
            <div className="overlay-gradient"></div>
            <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>

                {/* Mobile Header */}
                <div className="grid-span-8" style={{textAlign: 'center', marginBottom: 24}}>
                    <Title level={1} className="telefon" style={{color: 'var(--color-white)', margin: 0}}>
                        <MobileOutlined style={{marginRight: 12, color: 'var(--color-light-green)'}} />
                        Dołek #{selectedHole}
                    </Title>
                    {holeData && (
                        <div>
                            <Text style={{color: 'var(--white-60)', fontSize: '16px'}}>
                                Par: {holeData.par}
                            </Text>
                            {holeData.withBonus && (
                                <div style={{marginTop: 8}}>
                                    <GiftOutlined style={{color: 'var(--color-light-green)', marginRight: 4}} />
                                    <Text style={{color: 'var(--color-light-green)', fontSize: '14px'}}>
                                        Dołek z bonusem!
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* No NFC Warning */}
                {!nfcTagId && (
                    <div className="grid-span-8" style={{marginBottom: 24}}>
                        <Alert
                            message="Brak ID gracza!"
                            description="Zeskanuj kod QR z recepcji aby rozpocząć grę"
                            type="warning"
                            icon={<QrcodeOutlined />}
                            showIcon
                            style={{
                                backgroundColor: 'var(--white-10)',
                                border: '1px solid #ff6b6b',
                                color: 'var(--color-white)'
                            }}
                        />
                    </div>
                )}

                {/* Hole Selector */}
                <div className="grid-span-8" style={{marginBottom: 24}}>
                    <Card className="card">
                        <div className="field">
                            <div className="field__label">
                                <FlagOutlined style={{marginRight: 4}} />
                                Wybierz dołek
                            </div>
                            <Select
                                size="large"
                                value={selectedHole}
                                onChange={handleHoleChange}
                                disabled={loading}
                                style={{width: '100%'}}
                                className="mobile-select"
                            >
                                {holes.map(hole => (
                                    <Option key={hole.id} value={hole.number}>
                                        Dołek {hole.number} (Par {hole.par})
                                        {hole.withBonus && (
                                            <span style={{color: 'var(--color-light-green)'}}>
                                                {' '}🎁 Bonus
                                            </span>
                                        )}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Card>
                </div>

                {/* Player Info & Score Input */}
                {currentPlayer && (
                    <div className="grid-span-8" style={{marginBottom: 24}}>
                        <Card className="card">
                            {/* Player Stats */}
                            <div style={{textAlign: 'center', marginBottom: 24}}>
                                <div style={{marginBottom: 16}}>
                                    <AvatarDisplay
                                        avatarId={
                                            currentPlayer.avatar?.numericId ||
                                            currentPlayer.avatarId ||
                                            1
                                        }
                                        size={80}
                                        showBorder={true}
                                    />
                                </div>

                                <Title level={3} className="telefon" style={{color: 'var(--color-white)', margin: '0 0 8px 0'}}>
                                    "{currentPlayer.groupName || 'Grupa'}"
                                </Title>

                                {currentPlayer.playerNames && currentPlayer.playerNames.length > 0 && (
                                    <Text style={{color: 'var(--white-60)', fontSize: '14px', display: 'block', marginBottom: 16}}>
                                        Gracze: {currentPlayer.playerNames.join(', ')} ({currentPlayer.playerNames.length} osób)
                                    </Text>
                                )}

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '16px',
                                    marginBottom: '24px'
                                }}>
                                    <div style={{
                                        background: 'var(--white-10)',
                                        padding: '12px',
                                        borderRadius: 'var(--radius-8)',
                                        border: '1px solid var(--white-30)'
                                    }}>
                                        <Statistic
                                            title={<Text style={{color: 'var(--white-60)', fontSize: '12px'}}>Łączny wynik</Text>}
                                            value={currentPlayer.totalScore || 0}
                                            suffix="pkt"
                                            valueStyle={{color: 'var(--color-white)', fontSize: '20px'}}
                                        />
                                    </div>
                                    <div style={{
                                        background: 'var(--white-10)',
                                        padding: '12px',
                                        borderRadius: 'var(--radius-8)',
                                        border: '1px solid var(--white-30)'
                                    }}>
                                        <Statistic
                                            title={<Text style={{color: 'var(--white-60)', fontSize: '12px'}}>Dołki</Text>}
                                            value={`${currentPlayer.holes?.length || 0}/18`}
                                            valueStyle={{color: 'var(--color-white)', fontSize: '20px'}}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Group Score Input */}
                            <Title level={5} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 20}}>
                                <TeamOutlined style={{marginRight: 8}} />
                                Wyniki dla dołka #{selectedHole} (Par {holeData?.par || '?'})
                            </Title>

                            <Form form={form} layout="vertical" onFinish={handleScoreSubmit}>
                                <Row gutter={[12, 12]}>
                                    {currentPlayer.playerNames.map((playerName, index) => (
                                        <Col xs={24} sm={12} key={playerName}>
                                            <div style={{
                                                background: 'var(--white-10)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '12px',
                                                border: '1px solid var(--white-30)',
                                                textAlign: 'center'
                                            }}>
                                                <UserOutlined style={{
                                                    fontSize: '18px',
                                                    color: 'var(--color-white)',
                                                    marginBottom: '6px'
                                                }} />
                                                <Text style={{
                                                    color: 'var(--color-white)',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '8px'
                                                }}>
                                                    {playerName}
                                                </Text>
                                                <Form.Item
                                                    name={playerName}
                                                    rules={[
                                                        { required: true, message: 'Wprowadź liczbę ruchów' },
                                                        { type: 'number', min: 1, max: 6, message: 'Od 1 do 6 ruchów' }
                                                    ]}
                                                    style={{marginBottom: 0}}
                                                >
                                                    <InputNumber
                                                        min={1}
                                                        max={6}
                                                        size="large"
                                                        placeholder="Ruchy"
                                                        style={{
                                                            width: '100%',
                                                            textAlign: 'center',
                                                            fontSize: '16px'
                                                        }}
                                                        disabled={loading}
                                                        onChange={(value) => handleScoreChange(playerName, value)}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>

                                {/* Pole bonusu jeśli dołek go obsługuje */}
                                {holeData?.withBonus && (
                                    <div style={{marginTop: '20px'}}>
                                        <div style={{
                                            background: 'var(--color-light-green-20)',
                                            borderRadius: 'var(--radius-8)',
                                            padding: '16px',
                                            border: '2px solid var(--color-light-green)',
                                            textAlign: 'center'
                                        }}>
                                            <GiftOutlined style={{
                                                fontSize: '20px',
                                                color: 'var(--color-light-green)',
                                                marginBottom: '8px'
                                            }} />
                                            <Text style={{
                                                color: 'var(--color-white)',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                display: 'block',
                                                marginBottom: '12px'
                                            }}>
                                                Bonus dla grupy
                                            </Text>
                                            <InputNumber
                                                min={-10}
                                                max={10}
                                                size="large"
                                                placeholder="0"
                                                value={bonusScore}
                                                style={{
                                                    width: '120px',
                                                    textAlign: 'center',
                                                    fontSize: '18px'
                                                }}
                                                disabled={loading}
                                                onChange={handleBonusChange}
                                            />
                                            <div style={{marginTop: '8px'}}>
                                                <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                                                    Ujemne wartości odejmują punkty
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div style={{textAlign: 'center', marginTop: '20px'}}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        htmlType="submit"
                                        disabled={loading || !isFormComplete}
                                        loading={loading}
                                        icon={<CheckOutlined />}
                                        className="button primary mobile-big"
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            fontSize: '16px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {loading ? 'Zapisywanie...' : 'Zapisz wyniki grupy'}
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </div>
                )}

                {/* Wyniki graczy - Individual Leaderboard */}
                <div className="grid-span-8" style={{marginBottom: 24}}>
                    <Card className="card">
                        <Title level={4} style={{color: 'var(--color-white)', marginBottom: 16}}>
                            <TrophyOutlined style={{marginRight: 8}} />
                            Wyniki graczy
                        </Title>

                        {getIndividualPlayersLeaderboard().length === 0 ? (
                            <div style={{textAlign: 'center', padding: '20px 0'}}>
                                <InfoCircleOutlined style={{fontSize: 32, color: 'var(--white-40)', marginBottom: 8}} />
                                <Text style={{color: 'var(--white-60)'}}>
                                    Brak wyników graczy
                                </Text>
                            </div>
                        ) : (
                            <div>
                                {getIndividualPlayersLeaderboard().map((player, index) => {
                                    const position = index + 1;
                                    const isFromCurrentGroup = player.isCurrentPlayer;

                                    return (
                                        <div
                                            key={`${player.groupId}-${player.playerName}`}
                                            style={{
                                                background: isFromCurrentGroup ? 'var(--color-light-green-20)' : 'var(--white-10)',
                                                border: isFromCurrentGroup ? '2px solid var(--color-light-green)' : '1px solid var(--white-30)',
                                                borderRadius: 'var(--radius-8)',
                                                padding: '12px',
                                                marginBottom: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}
                                        >
                                            {/* Pozycja */}
                                            <div style={{
                                                background: position === 1 ? '#FFD700' :
                                                    position === 2 ? '#C0C0C0' :
                                                        position === 3 ? '#CD7F32' : 'var(--white-40)',
                                                color: position <= 3 ? '#000' : 'var(--color-white)',
                                                borderRadius: '50%',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                flexShrink: 0
                                            }}>
                                                {position === 1 ? '👑' : position}
                                            </div>

                                            {/* Awatar grupy */}
                                            <AvatarDisplay
                                                avatarId={player.avatarId || 1}
                                                size={32}
                                                showBorder={false}
                                            />

                                            {/* Informacje o graczu */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Text style={{
                                                    color: 'var(--color-white)',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {player.playerName}
                                                    {isFromCurrentGroup && ' (Twoja grupa)'}
                                                </Text>
                                                <Text style={{
                                                    color: 'var(--white-60)',
                                                    fontSize: '11px',
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    "{player.groupName || 'Nieznana grupa'}"
                                                </Text>
                                            </div>

                                            {/* Wyniki gracza */}
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Text style={{
                                                    color: isFromCurrentGroup ? 'var(--color-light-green)' : 'var(--color-white)',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    display: 'block'
                                                }}>
                                                    {player.totalScore} pkt
                                                </Text>
                                                <Text style={{
                                                    color: 'var(--white-60)',
                                                    fontSize: '10px'
                                                }}>
                                                    {player.totalStrokes} ruchów • {player.holesPlayed}/18
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Podsumowanie dla aktualnej grupy */}
                                {currentPlayer && (
                                    <div style={{
                                        textAlign: 'center',
                                        marginTop: '16px',
                                        padding: '8px',
                                        background: 'var(--white-05)',
                                        borderRadius: 'var(--radius-4)',
                                        border: '1px solid var(--white-20)'
                                    }}>
                                        <Text style={{color: 'var(--white-60)', fontSize: '11px'}}>
                                            Gracze z Twojej grupy: {currentPlayer.playerNames?.join(', ')}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Debug Info */}
                <div className="grid-span-8">
                    <MobileDebugInfo
                        nfcTagId={nfcTagId}
                        currentPlayer={currentPlayer}
                        playerScores={playerScores}
                        showSuccess={showSuccess}
                        showError={showError}
                        showInfo={showInfo}
                    />
                </div>
            </div>
        </div>
    );
};

export default MobileTerminal;